import { AlertCircle, ChevronRight, Inbox, LoaderCircle } from "lucide-react";
import { useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import type { DashboardRankingSort } from "@/features/dashboard/types";

const RANKING_DRAWER_CSS = `
  .dashboard-ranking-drawer {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    gap: 0.75rem;
    min-height: 0;
    min-width: 0;
    overflow: hidden;
  }

  .dashboard-ranking-drawer__toolbar {
    align-items: center;
    display: flex;
    flex: 0 0 auto;
    gap: 0.75rem;
    justify-content: space-between;
    min-width: 0;
  }

  .dashboard-ranking-drawer__count {
    color: var(--login-muted);
    font-family: "DM Sans", sans-serif;
    font-size: 0.8rem;
    white-space: nowrap;
  }

  .dashboard-ranking-drawer__select {
    appearance: none;
    background: var(--login-surface);
    border: 1px solid var(--login-border);
    border-radius: 0.45rem;
    color: var(--login-text);
    font-family: "DM Sans", sans-serif;
    font-size: 0.82rem;
    font-weight: 700;
    min-height: 44px;
    min-width: 0;
    padding: 0 2rem 0 0.7rem;
  }

  .dashboard-ranking-drawer__scroll {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    min-height: 0;
    min-width: 0;
    overflow-x: hidden;
    overflow-y: auto;
    overscroll-behavior: contain;
    scrollbar-color: color-mix(in srgb, var(--login-border) 75%, var(--login-muted)) transparent;
    scrollbar-width: thin;
  }

  .dashboard-ranking-drawer__row {
    align-items: center;
    background: transparent;
    border: 0;
    border-top: 1px solid var(--login-border);
    color: var(--login-text);
    cursor: pointer;
    display: grid;
    flex: 0 0 auto;
    font: inherit;
    gap: 0.6rem;
    grid-template-columns: 2rem minmax(0, 1fr) minmax(0, auto) 1.25rem;
    min-height: 56px;
    min-width: 0;
    padding: 0.7rem 0.2rem;
    text-align: left;
    transition:
      background-color 140ms ease,
      color 140ms ease,
      transform 120ms ease;
    width: 100%;
  }

  .dashboard-ranking-drawer__row:first-child { border-top: 0; }
  .dashboard-ranking-drawer__row:hover {
    background: color-mix(in srgb, var(--login-accent) 7%, var(--login-surface));
  }
  .dashboard-ranking-drawer__row:focus-visible,
  .dashboard-ranking-drawer__select:focus-visible,
  .dashboard-ranking-drawer__action:focus-visible {
    outline: 2px solid var(--login-accent);
    outline-offset: 2px;
  }
  .dashboard-ranking-drawer__row:active { transform: translateY(1px); }

  .dashboard-ranking-drawer__position {
    color: var(--login-accent);
    font-family: "DM Mono", monospace;
    font-size: 0.82rem;
    font-variant-numeric: tabular-nums;
    font-weight: 700;
    text-align: center;
    white-space: nowrap;
  }

  .dashboard-ranking-drawer__name {
    color: var(--login-text);
    font-family: "DM Sans", sans-serif;
    font-size: 0.92rem;
    font-weight: 700;
    line-height: 1.25;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .dashboard-ranking-drawer__metrics {
    align-items: end;
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem 0.8rem;
    justify-content: flex-end;
    min-width: 0;
  }

  .dashboard-ranking-drawer__metric {
    display: grid;
    gap: 0.12rem;
    justify-items: end;
    min-width: 0;
  }

  .dashboard-ranking-drawer__metric-label {
    color: var(--login-muted);
    font-family: "DM Sans", sans-serif;
    font-size: 0.64rem;
    font-weight: 800;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    white-space: nowrap;
  }

  .dashboard-ranking-drawer__metric-value {
    color: var(--login-text);
    font-family: "DM Mono", monospace;
    font-size: 0.78rem;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  .dashboard-ranking-drawer__action,
  .dashboard-ranking-drawer__state-action {
    align-items: center;
    background: var(--login-surface);
    border: 1px solid var(--login-border);
    border-radius: 0.5rem;
    color: var(--login-text);
    cursor: pointer;
    display: inline-flex;
    font-family: "DM Sans", sans-serif;
    font-size: 0.84rem;
    font-weight: 700;
    justify-content: center;
    min-height: 44px;
    padding-inline: 0.9rem;
    transition:
      background-color 140ms ease,
      color 140ms ease,
      opacity 140ms ease,
      transform 120ms ease;
  }

  .dashboard-ranking-drawer__action {
    flex: 0 0 auto;
    margin-top: 0.5rem;
    width: 100%;
  }
  .dashboard-ranking-drawer__action:disabled { cursor: wait; opacity: 0.62; }
  .dashboard-ranking-drawer__action:not(:disabled):active,
  .dashboard-ranking-drawer__state-action:active { transform: translateY(1px); }

  .dashboard-ranking-drawer__state {
    align-items: center;
    color: var(--login-muted);
    display: grid;
    flex: 1 1 auto;
    font-family: "DM Sans", sans-serif;
    font-size: 0.9rem;
    gap: 0.65rem;
    justify-items: center;
    min-height: 11rem;
    padding: 1.5rem;
    text-align: center;
  }

  @media (max-width: 390px) {
    .dashboard-ranking-drawer__toolbar { align-items: stretch; flex-direction: column; }
    .dashboard-ranking-drawer__select { width: 100%; }
    .dashboard-ranking-drawer__row {
      grid-template-columns: 1.75rem minmax(0, 1fr) 1.1rem;
      gap: 0.45rem;
    }
    .dashboard-ranking-drawer__metrics {
      grid-column: 2 / -1;
      justify-content: flex-start;
    }
    .dashboard-ranking-drawer__row > svg {
      grid-column: 3;
      grid-row: 1;
      justify-self: end;
    }
    .dashboard-ranking-drawer__metric { justify-items: start; }
  }

  @media (max-width: 320px) {
    .dashboard-ranking-drawer__metrics { display: grid; grid-template-columns: minmax(0, 1fr); }
    .dashboard-ranking-drawer__metric-value { font-size: 0.72rem; }
  }

  @media (prefers-reduced-motion: reduce) {
    .dashboard-ranking-drawer__row,
    .dashboard-ranking-drawer__action,
    .dashboard-ranking-drawer__state-action {
      transition-duration: 0.01ms;
      transform: none !important;
    }
  }
`;

export interface DashboardRankingMetricModel {
  label: string;
  value: string;
  accessibleLabel?: string;
}

export interface DashboardRankingRowModel {
  id: string;
  name: string;
  metrics: readonly DashboardRankingMetricModel[];
}

export interface DashboardRankingSortOption {
  value: DashboardRankingSort;
  label: string;
}

export interface DashboardRankingDrawerProps {
  title: string;
  items: readonly DashboardRankingRowModel[];
  sort: DashboardRankingSort;
  sortOptions: readonly DashboardRankingSortOption[];
  isPending?: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  error?: unknown;
  restoreScrollTop?: number;
  restoreFocusItemId?: string | null;
  onSortChange: (sort: DashboardRankingSort) => void;
  onOpenDetail: (item: DashboardRankingRowModel, scrollTop: number) => void;
  onLoadMore: () => void | Promise<unknown>;
  onRetry?: () => void;
  onFocusRestored?: () => void;
}

const safeTestId = (value: string) => value.replace(/[^a-zA-Z0-9_-]+/g, "-");

const rowAccessibleName = (item: DashboardRankingRowModel) => {
  const metrics = item.metrics
    .map(
      (metric) => metric.accessibleLabel ?? `${metric.label}: ${metric.value}`,
    )
    .join("; ");
  return `Abrir detalhes de ${item.name}${metrics ? `; ${metrics}` : ""}`;
};

const nounFromTitle = (title: string) =>
  title.trim().split(/\s+/u).at(-1)?.toLocaleLowerCase("pt-BR") ?? "itens";

const stateStyle: CSSProperties = {
  alignItems: "center",
  display: "inline-flex",
  gap: "0.5rem",
};

export function DashboardRankingDrawer({
  title,
  items,
  sort,
  sortOptions,
  isPending = false,
  hasNextPage,
  isFetchingNextPage,
  error,
  restoreScrollTop = 0,
  restoreFocusItemId,
  onSortChange,
  onOpenDetail,
  onLoadMore,
  onRetry,
  onFocusRestored,
}: DashboardRankingDrawerProps) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const pageRequestRef = useRef<Promise<unknown> | null>(null);
  const [localPagePending, setLocalPagePending] = useState(false);
  const pagePending = isFetchingNextPage || localPagePending;
  const entityNoun = nounFromTitle(title);

  useLayoutEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = restoreScrollTop;
  }, [restoreScrollTop]);

  useLayoutEffect(() => {
    if (!restoreFocusItemId || !scrollRef.current) return;
    const row = Array.from(
      scrollRef.current.querySelectorAll<HTMLButtonElement>(
        "[data-dashboard-ranking-item-id]",
      ),
    ).find(
      (candidate) =>
        candidate.dataset.dashboardRankingItemId === restoreFocusItemId,
    );
    if (!row) return;
    row.focus();
    onFocusRestored?.();
  }, [items, onFocusRestored, restoreFocusItemId]);

  const requestNextPage = () => {
    if (pagePending || pageRequestRef.current) return;
    setLocalPagePending(true);
    const request = Promise.resolve(onLoadMore());
    pageRequestRef.current = request;
    void request.finally(() => {
      if (pageRequestRef.current !== request) return;
      pageRequestRef.current = null;
      setLocalPagePending(false);
    });
  };

  return (
    <div className="dashboard-ranking-drawer">
      <style>{RANKING_DRAWER_CSS}</style>
      <div className="dashboard-ranking-drawer__toolbar">
        <span className="dashboard-ranking-drawer__count">
          {items.length.toLocaleString("pt-BR")} exibidos
        </span>
        {sortOptions.length > 1 ? (
          <select
            className="dashboard-ranking-drawer__select"
            aria-label={`Ordenar ${title}`}
            value={sort}
            onChange={(event) =>
              onSortChange(event.currentTarget.value as DashboardRankingSort)
            }
          >
            {sortOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        ) : null}
      </div>

      <div
        ref={scrollRef}
        className="dashboard-ranking-drawer__scroll"
        data-dashboard-scroll-owner="ranking"
        data-testid="dashboard-ranking-scroll-region"
      >
        {isPending && items.length === 0 ? (
          <div
            className="dashboard-ranking-drawer__state"
            role="status"
            aria-busy="true"
          >
            <span style={stateStyle}>
              <LoaderCircle aria-hidden="true" size={20} />
              Carregando ranking…
            </span>
          </div>
        ) : error && items.length === 0 ? (
          <div className="dashboard-ranking-drawer__state" role="alert">
            <span style={stateStyle}>
              <AlertCircle aria-hidden="true" size={20} />
              Não foi possível carregar o ranking.
            </span>
            {onRetry ? (
              <button
                type="button"
                className="dashboard-ranking-drawer__state-action"
                onClick={onRetry}
              >
                Tentar novamente
              </button>
            ) : null}
          </div>
        ) : items.length === 0 ? (
          <div className="dashboard-ranking-drawer__state" role="status">
            <span style={stateStyle}>
              <Inbox aria-hidden="true" size={20} />
              Nenhum registro neste período.
            </span>
          </div>
        ) : (
          <div style={{ minWidth: 0 }}>
            {error ? (
              <div className="dashboard-ranking-drawer__state" role="alert">
                Alguns registros não puderam ser atualizados.
              </div>
            ) : null}
            {items.map((item, index) => (
              <button
                key={item.id}
                type="button"
                className="dashboard-ranking-drawer__row"
                aria-label={rowAccessibleName(item)}
                data-testid={`dashboard-ranking-row-${safeTestId(item.id)}`}
                data-dashboard-ranking-item-id={item.id}
                onClick={() =>
                  onOpenDetail(item, scrollRef.current?.scrollTop ?? 0)
                }
              >
                <span className="dashboard-ranking-drawer__position">
                  {index + 1}
                </span>
                <span
                  className="dashboard-ranking-drawer__name"
                  title={item.name}
                  style={{
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {item.name}
                </span>
                <span className="dashboard-ranking-drawer__metrics">
                  {item.metrics.map((metric, metricIndex) => (
                    <span
                      key={`${metric.label}-${metricIndex}`}
                      className="dashboard-ranking-drawer__metric"
                    >
                      <span className="dashboard-ranking-drawer__metric-label">
                        {metric.label}
                      </span>
                      <span
                        className="dashboard-ranking-drawer__metric-value"
                        title={metric.accessibleLabel ?? metric.value}
                      >
                        {metric.value}
                      </span>
                    </span>
                  ))}
                </span>
                <ChevronRight aria-hidden="true" size={17} />
              </button>
            ))}
          </div>
        )}

        {hasNextPage ? (
          <button
            type="button"
            className="dashboard-ranking-drawer__action"
            aria-busy={pagePending ? "true" : "false"}
            aria-label={
              pagePending
                ? `Carregando mais ${entityNoun}`
                : `Carregar mais ${entityNoun}`
            }
            data-testid="dashboard-ranking-load-more-sentinel"
            disabled={pagePending}
            onClick={requestNextPage}
          >
            {pagePending ? (
              <>
                <LoaderCircle aria-hidden="true" size={17} />
                Carregando…
              </>
            ) : (
              "Carregar mais"
            )}
          </button>
        ) : null}
      </div>
    </div>
  );
}
